import styles from './index.module.css';
import { ReactComponent as Back } from '../../../../../Assets/BOB/v2/back.svg';
import { ReactComponent as RightArrow } from '../../../../../Assets/BOB/v2/right-arrow.svg';
import quizBanner from '../../../../../Assets/BOB/v2/quiz-banner.png';
import addAccountImg from '../../../../../Assets/BOB/v2/add-account.png';
import { Divider, Switch } from 'antd';
import ListTile from '../../../Components/listTile';
import { AssetCategory } from '../../../utils/constants';
import { getTotalSavingsAmount } from '../../../utils/savingsUtils';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../../../../../App';
import { getTotalStockValue } from '../../../utils/stockUtils';
import { getTotalFixedDepositValue } from '../../../utils/fixedDepositUtils';
import { getNpsTotalValue } from '../../../utils/npsUtils';
import { formatToINR } from '../../../../../Utils/utils';
import {
  checkAccountRevokeProgress,
  doesFiTypeExist,
  getAssetLinkedAccounts,
  handleOnAssetClick,
  isDataPending,
  linkNewAccount,
} from '../../../utils/assetUtils';
import { getMutualFundTotalValueRaw } from '../../../utils/mutualFundUtils';
import { ReactComponent as Plus } from '../../../../../Assets/BOB/v2/plus.svg';
import CashflowCategory from '../../components/cashflow/cashflowCategory';
import { EVENTCODES, pushEventToParent } from '../../../../../Utils/eventUtils';
import { FlowType } from '../../../../../Utils/constants';
import { ROUTES } from '../../../../../Utils/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import Toast from '../../../common/Toast';
import { LoadingOutlined } from '@ant-design/icons';
import { getUserProfile } from '../../../../../Utils/storageUtils';
import { getFinancialLiteracyQuizTypeByDob } from '../../../quiz/utils/quizUtils';
import Spending from '../../components/spending/spending';
import NudgesCard from '../../../AssetsDashboard/NudgesCard';
import {
  showAssetDashboardDataNote,
  showNudgesCard,
} from '../../../utils/configUtils';
import { ReactComponent as BankAccountBOB } from '../../../../../Assets/BOB/v2/dashboard/bank-acc.svg';
import { ReactComponent as FixedDepositBOB } from '../../../../../Assets/BOB/v2/dashboard/fixed-deposit.svg';
import { ReactComponent as MutualFundBOB } from '../../../../../Assets/BOB/v2/dashboard/mutual-fund.svg';
import { ReactComponent as StockBOB } from '../../../../../Assets/BOB/v2/dashboard/portfolio-stocks.svg';
import { ReactComponent as NpsBOB } from '../../../../../Assets/BOB/v2/dashboard/nps.svg';
import Button from '../../components/button/button';
import { startCase } from 'es-toolkit';
import { handleOnAssetClickType } from '../../../utils/types';

const AssetDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState<string>('');
  const shouldShowNudgesCard = showNudgesCard();
  const shouldShowAssetDashboardDataNote = showAssetDashboardDataNote();
  const [isAssetRevoking, setisAssetRevoking] = useState<
    AssetCategory | undefined
  >(() => location.state?.isRevoking);
  const {
    statusData,
    data,
    datafromAnalyticsStatus,
    mutualFundsRawData,
    isAmountVisible,
    setIsAmountVisible,
    forceRender,
    isBackEventSent,
    setPreFetchedLoadingForAssets,
    startAdditionalAccountJourney,
    setIsAdditionalAccountJourney,
    setAAInternalStep,
    setCurrentRoute,
    preFetchedLoadingForAssets,
    setBackButtonHandler,
    isAssetDashboardRendered,
  }: any = useContext(AppContext);
  const username = data.current.USERNAME;
  const title = username
    ? `Hey ${
        username.split(' ')[0].length > 3
          ? startCase(username.split(' ')[0])
          : startCase(username)
      } 👋`
    : null;

  // Press back handling
  const handleBackNavigation = useCallback(() => {
    pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
  }, []);

  useEffect(() => {
    isAssetDashboardRendered.current = true;
    window.onpopstate = () => {
      // Push the user forward again to prevent back
      window.history.pushState(null, '', window.location.href);
    };
    setBackButtonHandler(() => handleBackNavigation);
    return () => {
      window.onpopstate = null;
    };
  }, [handleBackNavigation, isAssetDashboardRendered, setBackButtonHandler]);

  const isLoading = useCallback(
    (category: AssetCategory) => {
      const dataLoading =
        doesFiTypeExist(category, statusData) &&
        isDataPending(category, statusData);
      const accountRevokeProgress = checkAccountRevokeProgress(
        statusData,
        category
      );
      return (
        accountRevokeProgress ||
        dataLoading ||
        Boolean(preFetchedLoadingForAssets === category) ||
        isAssetRevoking === category
      );
    },
    [preFetchedLoadingForAssets, statusData, forceRender, isAssetRevoking]
  );

  useEffect(() => {
    if (location.state?.isRevoking) {
      navigate(location.pathname, {
        replace: true,
        state: null,
      });
    }
  }, [location.pathname, location.state?.isRevoking, navigate]);

  useEffect(() => {
    if (!isAssetRevoking) return;
    if (checkAccountRevokeProgress(statusData, isAssetRevoking)) {
      setisAssetRevoking(undefined);
    }
  }, [isAssetRevoking, statusData, forceRender]);

  // Navigation is blocked only while data is genuinely still processing (or a
  // prefetch is in flight) — NOT merely because another account's consent is
  // being revoked. That keeps the active accounts reachable.
  const isClickBlocked = useCallback(
    (category: AssetCategory) =>
      (doesFiTypeExist(category, statusData) &&
        isDataPending(category, statusData)) ||
      preFetchedLoadingForAssets === category,
    [preFetchedLoadingForAssets, statusData, forceRender]
  );

  const handleClick: handleOnAssetClickType = (
    asset,
    events,
    linkedAccounts,
    flowTypeAsset,
    route
  ) => {
    handleOnAssetClick({
      asset,
      statusData,
      setToast,
      isBackEventSent,
      linkedAccounts,
      events,
      setPreFetchedLoadingForAssets,
      startAdditionalAccountJourney,
      flowTypeAsset,
      setIsAdditionalAccountJourney,
      setAAInternalStep,
      route,
      setCurrentRoute,
      navigate,
    });
  };

  const handleFinancialLiteracyQuizClick = () => {
    const userProfile = getUserProfile();
    const selectedQuizType = getFinancialLiteracyQuizTypeByDob(
      userProfile?.maskedDateOfBirth
    );
    pushEventToParent({
      eventCode: EVENTCODES.FINANCIAL_LITERACY_QUIZ_CLICKED,
      meta: { selectedQuizType },
    });
    navigate(ROUTES.COMMON_TENANT_FINANCIAL_QUIZ_RULES, {
      state: { selectedQuizType },
    });
    setCurrentRoute(ROUTES.COMMON_TENANT_FINANCIAL_QUIZ_RULES);
  };

  const assets = useMemo(
    () => [
      {
        type: AssetCategory.BANKS,
        title: 'Bank Account',
        subtitle: getTotalSavingsAmount(statusData, datafromAnalyticsStatus),
        Icon: BankAccountBOB,
        totalAccounts: getAssetLinkedAccounts(
          statusData,
          AssetCategory.BANKS,
          'ACTIVE'
        ),
        eventsOnClick: {
          onAssetClickEvent: EVENTCODES.BANK_ACCOUNT_CLICKED,
          clickToLinkAssetEvent: EVENTCODES.ADD_ACCOUNT_BANKS,
        },
        flowtype: FlowType.BANKS,
        route: ROUTES.COMMON_TENANT_SPENDS,
        loading: isLoading(AssetCategory.BANKS),
      },
      {
        type: AssetCategory.STOCKS,
        title: 'Stocks',
        subtitle: getTotalStockValue(statusData, data, datafromAnalyticsStatus),
        Icon: StockBOB,
        totalAccounts: getAssetLinkedAccounts(
          statusData,
          AssetCategory.STOCKS,
          'ACTIVE'
        ),
        eventsOnClick: {
          onAssetClickEvent: EVENTCODES.STOCKS_CLICKED,
          clickToLinkAssetEvent: EVENTCODES.STOCKS_NEW_USER,
        },
        flowtype: FlowType.STOCKS,
        route: ROUTES.COMMON_TENANT_STOCKS,
        loading: isLoading(AssetCategory.STOCKS),
      },
      {
        type: AssetCategory.FIXED_DEPOSIT,
        title: 'Fixed Deposit',
        subtitle: getTotalFixedDepositValue(
          statusData,
          data,
          datafromAnalyticsStatus
        ),
        Icon: FixedDepositBOB,
        totalAccounts: getAssetLinkedAccounts(
          statusData,
          AssetCategory.FIXED_DEPOSIT,
          'ACTIVE'
        ),
        eventsOnClick: {
          onAssetClickEvent: EVENTCODES.FD_CLICKED,
          clickToLinkAssetEvent: EVENTCODES.FD_LINK_NOW_CLICKED,
        },
        flowtype: FlowType.TERM_DEPOSITS,
        route: ROUTES.COMMON_TENANT_FDRD,
        loading: isLoading(AssetCategory.FIXED_DEPOSIT),
      },
      {
        type: AssetCategory.MUTUAL_FUNDS,
        title: 'Mutual Fund',
        subtitle: getMutualFundTotalValueRaw(mutualFundsRawData, statusData),
        Icon: MutualFundBOB,
        totalAccounts: getAssetLinkedAccounts(
          statusData,
          AssetCategory.MUTUAL_FUNDS,
          'ACTIVE'
        ),
        eventsOnClick: {
          onAssetClickEvent: EVENTCODES.MUTUAL_FUNDS_CLICKED,
          clickToLinkAssetEvent: EVENTCODES.ADD_ACCOUNT_MF,
        },
        flowtype: FlowType.MUTUAL_FUNDS,
        route: ROUTES.COMMON_TENANT_MF,
        loading: isLoading(AssetCategory.MUTUAL_FUNDS),
      },
      {
        type: AssetCategory.NPS,
        title: 'NPS',
        subtitle: getNpsTotalValue(statusData, data, datafromAnalyticsStatus),
        Icon: NpsBOB,
        totalAccounts: getAssetLinkedAccounts(
          statusData,
          AssetCategory.NPS,
          'ACTIVE'
        ),
        eventsOnClick: {
          onAssetClickEvent: EVENTCODES.NPS_CLICKED,
          clickToLinkAssetEvent: EVENTCODES.NPS_LINK_NOW_CLICKED,
        },
        flowtype: FlowType.NPS,
        route: ROUTES.COMMON_TENANT_NPS,
        loading: isLoading(AssetCategory.NPS),
      },
    ],
    [
      statusData,
      datafromAnalyticsStatus,
      isLoading,
      data,
      mutualFundsRawData,
      forceRender,
      isAssetRevoking,
    ]
  );
  const getTotalNetWorth = assets.reduce((amount: number, asset) => {
    if(asset.subtitle) return amount + asset.subtitle;
    return amount;
  }, 0);

  useEffect(() => {
    if (!Boolean(preFetchedLoadingForAssets)) return;
    const category = preFetchedLoadingForAssets;
    const dataLoading =
      doesFiTypeExist(category, statusData) &&
      isDataPending(category, statusData);
    let timer: ReturnType<typeof setTimeout>;
    if (dataLoading) {
      timer = setTimeout(() => {
        setPreFetchedLoadingForAssets(null);
        const correspondingAsset = assets.find(
          (asset) => asset.type === category
        );
        if (!correspondingAsset) return;
        correspondingAsset.loading = dataLoading;
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [
    assets,
    preFetchedLoadingForAssets,
    setPreFetchedLoadingForAssets,
    statusData,
    forceRender,
  ]);

  return (
    <div className={styles.applayout}>
      <div className={`${styles.bannerContainer}`}>
        <div className={styles.contentLayout}>
          <div className={styles.portfolioHeader}>
            <Back className={styles.back} onClick={handleBackNavigation} />
            <div className={styles.switchContainer}>
              <p className={styles.switchLabel}>
                {!isAmountVisible ? 'Hide Balance' : 'Show Balance'}
              </p>
              <Switch
                value={isAmountVisible}
                onChange={() => setIsAmountVisible((prev: boolean) => !prev)}
                className={styles.switch}
              />
            </div>
          </div>
          <div className={styles.accountHolderContainer}>
            {title && <p className={styles.accountHolder}>{title}</p>}
            <p className={styles.accountSubtitle}>
              Your complete financial overview, all in one place
            </p>
          </div>
          <div className={styles.portfolioDetails}>
            <div className={styles.networthSection}>
              <p className={styles.summaryTitle}>Total Net Worth</p>
              <p className={styles.netWorth}>
                {formatToINR(getTotalNetWorth.toString(), isAmountVisible, 2)}
              </p>
            </div>
            <div className={styles.assetsList}>
              <p className={styles.summaryTitle}>Account Summary</p>

              <div className={styles.portfolioAssets}>
                {assets.map((asset) => {
                  const {
                    type,
                    title,
                    subtitle,
                    Icon,
                    totalAccounts,
                    eventsOnClick,
                    flowtype,
                    route,
                    loading,
                  } = asset;
                  const totalAccountsCount = totalAccounts.length;
                  let value: string;                  
                  if (totalAccountsCount === 0) {
                    value = loading ? 'Processing...' : 'Link Account';
                  } else if (subtitle && typeof subtitle === 'number') {
                    value = formatToINR(subtitle.toString(), isAmountVisible);
                    if (preFetchedLoadingForAssets === type) {
                      setPreFetchedLoadingForAssets();
                    }
                  } else {
                    value = 'Processing...';
                  }

                  const ActionIcon = isClickBlocked(type)
                    ? LoadingOutlined
                    : RightArrow;
                  return (
                    <button
                      className={styles.assetButton}
                      onClick={() => {
                        isClickBlocked(type)
                          ? setToast('Processing your data..')
                          : handleClick(
                              type,
                              eventsOnClick,
                              totalAccounts.length,
                              flowtype,
                              route
                            );
                      }}
                      key={type}
                    >
                      <ListTile
                        title={`${title} (${totalAccountsCount})`}
                        subtitle={<span className={`${value === 'Link Account' ? styles.valueAsset : ''}`}>{value}</span>}
                        actionIcon={
                          <ActionIcon className={styles.actionIcon} />
                        }
                        icon={<Icon className={styles.icon} />}
                        color='black'
                        variant='primary'
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.dashboardBody}>
        {assets[0].totalAccounts.length > 0 && (
          <CashflowCategory header="This Month's Cash Flow" variant='primary' />
        )}
        <img
          className={styles.quizBanner}
          src={quizBanner}
          alt='quiz-banner'
          width={'100%'}
          height={'100%'}
          loading='lazy'
          onClick={handleFinancialLiteracyQuizClick}
        />
        {assets[0].totalAccounts.length > 0 && <Spending />}
        <div className={`${styles.container} ${styles.accContainer}`}>
          <div className={styles.addAccountLayout}>
            <div className={styles.accWrapper}>
              <div className={styles.accConnectInfo}>
                <p className={styles.accHead}>Connect More accounts</p>
                <p className={styles.acclabel}>
                  Get a complete view of your investments, loans and more.
                </p>
              </div>
              <Button
                title='Add accounts'
                Icon={Plus}
                containerClass={styles.button}
                onClick={() =>
                  linkNewAccount({
                    asset: AssetCategory.BANKS,
                    setPreFetchedLoadingForAssets,
                    startAdditionalAccountJourney,
                    flowTypeAsset: FlowType.BANKS,
                    setIsAdditionalAccountJourney,
                    setAAInternalStep,
                    events: EVENTCODES.ADD_ACCOUNT_BANKS,
                  })
                }
                variant='primary'
              />
            </div>
            <img
              src={addAccountImg}
              alt='add-acc'
              width={'102px'}
              height={'96px'}
              loading='lazy'
            />
          </div>
        </div>
        {shouldShowNudgesCard && <NudgesCard />}
        {shouldShowAssetDashboardDataNote && (
          <div className={styles.note_card}>
            <span className={styles.note_dark}>Note:</span> Portfolio
            information shown here is for reference only. Data is aggregated
            from multiple sources via Account Aggregator and may contain delays,
            missing holdings, duplicates, or valuation differences.
          </div>
        )}
      </div>
      <Toast
        message={toast}
        visible={toast.length > 0}
        onClose={() => setToast('')}
      />
    </div>
  );
};

export default AssetDashboard;
